import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, RotateCcw, RotateCw } from "lucide-react";
import { streamUrl } from "@/lib/api";
import { Slider } from "@/components/ui/slider";

function formatTime(s) {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function MediaPlayer({ media, onProgress, onEnded, onCompleted }) {
  const isVideo = media.kind === "video";
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const completedRef = useRef(false);
  const lastReportRef = useRef(0);

  const src = streamUrl(media.id);

  useEffect(() => {
    setPlaying(false);
    setTime(0);
    setDuration(0);
    completedRef.current = false;
    lastReportRef.current = 0;
    // Auto-play ao trocar de faixa (clique do usuário conta como gesto válido).
    const el = ref.current;
    if (!el) return;
    el.load();
    const p = el.play();
    if (p && typeof p.then === "function") {
      p.then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  }, [media.id]);

  const onLoaded = () => {
    setDuration(ref.current?.duration || 0);
  };
  const onTime = () => {
    const cur = ref.current?.currentTime || 0;
    const dur = ref.current?.duration || 0;
    setTime(cur);
    // Reporta a cada 5s aproximadamente
    if (onProgress && (Math.abs(cur - lastReportRef.current) >= 5 || (dur && cur >= dur - 0.5))) {
      lastReportRef.current = cur;
      onProgress({ position: cur, duration: dur });
    }
    // Marca completado ao atingir 90%
    if (!completedRef.current && dur > 0 && cur >= 0.9 * dur) {
      completedRef.current = true;
      if (onCompleted) onCompleted();
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    if (!completedRef.current) {
      completedRef.current = true;
      if (onCompleted) onCompleted();
    }
    if (onEnded) onEnded();
  };

  const toggle = () => {
    if (!ref.current) return;
    if (ref.current.paused) {
      ref.current.play();
      setPlaying(true);
    } else {
      ref.current.pause();
      setPlaying(false);
    }
  };

  const seek = (v) => {
    if (!ref.current) return;
    ref.current.currentTime = v;
    setTime(v);
  };

  const skip = (delta) => {
    if (!ref.current) return;
    ref.current.currentTime = Math.max(0, Math.min((ref.current.duration || 0), ref.current.currentTime + delta));
  };

  const onVol = (v) => {
    const val = v / 100;
    setVolume(val);
    if (ref.current) ref.current.volume = val;
    if (val === 0) setMuted(true);
    else setMuted(false);
  };

  const toggleMute = () => {
    if (!ref.current) return;
    const m = !muted;
    setMuted(m);
    ref.current.muted = m;
  };

  const blockContext = (e) => e.preventDefault();

  return (
    <div
      data-testid="media-player"
      className="w-full bg-[#0A0A0A] text-white rounded-sm shadow-xl overflow-hidden"
      onContextMenu={blockContext}
    >
      <div className="px-6 pt-5 pb-2 border-b border-neutral-800 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.2em] text-[#E30613] mb-1">
            {isVideo ? "Vídeo" : "Áudio"}
          </div>
          <h3 className="text-lg font-semibold truncate" data-testid="media-title">
            {media.title}
          </h3>
        </div>
      </div>

      {isVideo ? (
        <div className="bg-black flex items-center justify-center">
          <video
            ref={ref}
            src={src}
            className="w-full max-h-[60vh]"
            onLoadedMetadata={onLoaded}
            onTimeUpdate={onTime}
            onEnded={handleEnded}
            onContextMenu={blockContext}
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture
            playsInline
            preload="metadata"
          />
        </div>
      ) : (
        <audio
          ref={ref}
          src={src}
          onLoadedMetadata={onLoaded}
          onTimeUpdate={onTime}
          onEnded={handleEnded}
          preload="metadata"
          className="hidden"
        />
      )}

      <div className="p-6 flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <span className="text-xs text-neutral-400 w-12 tabular-nums">{formatTime(time)}</span>
          <Slider
            data-testid="seek-slider"
            value={[time]}
            min={0}
            max={duration || 0}
            step={0.1}
            onValueChange={(v) => seek(v[0])}
            className="flex-1"
          />
          <span className="text-xs text-neutral-400 w-12 tabular-nums text-right">{formatTime(duration)}</span>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => skip(-10)}
              data-testid="skip-back-btn"
              className="p-2 rounded-sm hover:bg-neutral-800 transition-colors"
              aria-label="Voltar 10s"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
            <button
              onClick={toggle}
              data-testid="play-pause-btn"
              className="w-12 h-12 rounded-sm bg-[#E30613] hover:bg-[#B3040E] transition-colors flex items-center justify-center"
              aria-label={playing ? "Pausar" : "Reproduzir"}
            >
              {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
            </button>
            <button
              onClick={() => skip(10)}
              data-testid="skip-fwd-btn"
              className="p-2 rounded-sm hover:bg-neutral-800 transition-colors"
              aria-label="Avançar 10s"
            >
              <RotateCw className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-2 w-44">
            <button onClick={toggleMute} className="p-1.5 hover:bg-neutral-800 rounded-sm" aria-label="Mudo">
              {muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <Slider
              value={[muted ? 0 : volume * 100]}
              min={0}
              max={100}
              step={1}
              onValueChange={(v) => onVol(v[0])}
              className="flex-1"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
